/**
 * AsTooltipWrapper Component
 * 
 * A wrapper component that creates a tooltip attached to its child elements.
 * It supports multiple display modes: show-on-hover, toggle-on-click, or manual control.
 * Uses @floating-ui/dom for robust positioning with collision detection and boundary awareness.
 * 
 * Attributes:
 * - dict-key: Localization key for the tooltip text
 * - default-text: Default text to display if no translation is found
 * - vars: JSON array of values to fill {0}, {1}, … placeholders in the translated text
 * - show: When present, displays the tooltip
 * - placement: Position of the tooltip (top, bottom, left, right, etc.)
 * - show-on-hover: When present, shows tooltip on hover
 * - toggle-on-click: When present, toggles tooltip visibility on click
 * - close-on-click: When present (with show-on-hover), a click on the wrapped
 *   element dismisses the tooltip instead of leaving it open
 * - no-arrow: When present, hides the tooltip arrow (passed to as-tooltip)
 * - auto-flip: When present, allows the tooltip to flip position when near viewport edges
 * - offset: Distance in pixels from the trigger element (default: 8)
 */
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
import './as-tooltip.ts';
import styleText from './as-tooltip-wrapper.css?raw';
import { applyLocalizedText } from '../base.ts';
import { sendEv } from '../../data/events.ts';

export class AsTooltipWrapper extends HTMLElement {
  static readonly tag = 'as-tooltip-wrapper' as const;

  private _cleanup?: () => void;
  private _intersectionObserver?: IntersectionObserver;
  private _shouldBeVisible: boolean = false;
  private _flashTimeout?: number;
  private _portaledTooltip?: HTMLElement;
  private _pinned = false;
  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return ['dict-key', 'default-text', 'vars', 'show', 'placement', 'show-on-hover', 'toggle-on-click', 'close-on-click', 'no-arrow', 'offset', 'auto-flip', 'flash', 'flash-duration', 'flash-delay', 'active-tooltip'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsTooltipWrapper._sheet
    ];
  }

  /**
   * Map our placement terms to Floating UI placements
   */
  private _mapPlacement(placement: string) {
    const placementMap: { [key: string]: string } = {
      'top': 'top',
      'bottom': 'bottom',
      'left': 'left',
      'right': 'right',
      'top-left': 'top-end',
      'top-right': 'top-start',
      'bottom-left': 'bottom-end',
      'bottom-right': 'bottom-start'
    };
    return placementMap[placement] || 'bottom';
  }

  /**
   * Get offset value
   */
  private _getOffset(): number {
    return parseInt(this.getAttribute('offset') ?? '8');
  }

  /**
   * Flash the tooltip - show it briefly then hide it
   */
  private _flashTooltip(): void {
    // Clear any existing flash timeout
    if (this._flashTimeout) {
      clearTimeout(this._flashTimeout);
      this._flashTimeout = undefined;
    }

    // Get flash delay (default 0ms) and duration (default 2000ms)
    const delay = parseInt(this.getAttribute('flash-delay') ?? '0');
    const duration = parseInt(this.getAttribute('flash-duration') ?? '2000');

    // Wait for delay, then show the tooltip
    this._flashTimeout = window.setTimeout(() => {
      // Create and show the tooltip
      this._createPortaledTooltip();
      this._shouldBeVisible = true;
      this._portaledTooltip?.setAttribute('show', '');
      this._updateTooltipPosition();

      // Hide after duration and remove flash attribute
      this._flashTimeout = window.setTimeout(() => {
        this._shouldBeVisible = false;
        this._portaledTooltip?.removeAttribute('show');

        // Clean up autoUpdate when hiding
        if (this._cleanup) {
          this._cleanup();
          this._cleanup = undefined;
        }

        // Remove portaled tooltip
        this._removePortaledTooltip();

        // Remove the flash attribute to prevent re-triggering
        this.removeAttribute('flash');
        this._flashTimeout = undefined;
      }, duration);
    }, delay);
  }

  /**
   * Set up intersection observer to auto-hide tooltip when scrolled out of view
   */
  private _setupIntersectionObserver() {
    const wrapperElement = this.shadowRoot?.querySelector('.tooltip-wrapper') as HTMLElement;
    if (!wrapperElement) return;

    // Clean up existing observer
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }

    this._intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const tooltip = this._portaledTooltip;
        if (!tooltip) return;

        if (!entry.isIntersecting) {
          // Element is scrolled out of view - hide tooltip if visible
          if (tooltip.hasAttribute('show')) {
            tooltip.removeAttribute('show');
            // Clean up autoUpdate when hiding
            if (this._cleanup) {
              this._cleanup();
              this._cleanup = undefined;
            }
          }
        } else {
          // Element is back in view - restore tooltip if it should be visible
          if (this._shouldBeVisible && !tooltip.hasAttribute('show')) {
            tooltip.setAttribute('show', '');
            this._updateTooltipPosition();
          }
        }
      });
    }, {
      // Use a small margin to hide tooltips slightly before they go completely off-screen
      rootMargin: '-10px'
    });

    this._intersectionObserver.observe(wrapperElement);
  }

  /**
   * Update tooltip position using Floating UI
   */
  private _updateTooltipPosition() {
    const tooltip = this._portaledTooltip;
    const wrapperElement = this.shadowRoot?.querySelector('.tooltip-wrapper') as HTMLElement;

    if (!tooltip || !wrapperElement) return;

    const placement = this.getAttribute('placement') || 'bottom';
    const floatingPlacement = this._mapPlacement(placement);
    const offsetValue = this._getOffset();

    // Build middleware array conditionally
    const middleware = [offset(offsetValue)];

    // Only add flip if auto-flip attribute is present
    if (this.hasAttribute('auto-flip')) {
      middleware.push(flip());
    }

    middleware.push(shift({ padding: 5 }));

    // Clean up any existing autoUpdate
    if (this._cleanup) {
      this._cleanup();
    }

    // Set up intersection observer for auto-hiding
    this._setupIntersectionObserver();

    // Set up autoUpdate to handle scroll/resize events
    this._cleanup = autoUpdate(wrapperElement, tooltip, async () => {
      try {
        const { x, y } = await computePosition(wrapperElement, tooltip, {
          placement: floatingPlacement as any,
          strategy: 'fixed',
          middleware: middleware,
        });

        // Apply the computed position
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
          transform: 'none',
          position: 'fixed',
          zIndex: '10050',
        });
      } catch (error) {
        console.warn('Failed to position tooltip:', error);
      }
    });
  }

  /**
   * Set up the component template with a single source of truth approach
   */
  private _createTemplate(): DocumentFragment {
    const template = document.createElement('template');

    template.innerHTML = `
      <div class="tooltip-wrapper"
           ${this.hasAttribute('show-on-hover') ? 'data-hover="true"' : ''}
           ${this.hasAttribute('toggle-on-click') ? 'data-click="true"' : ''}
           ${this.hasAttribute('close-on-click') ? 'data-close-on-click="true"' : ''}>
        <slot></slot>
      </div>
    `;

    return template.content.cloneNode(true) as DocumentFragment;
  }

  /**
   * Create the portaled tooltip element in document.body.
   * If the wrapper has as-tooltip[name] children, the one matching nameOverride
   * (or the active-tooltip attribute, defaulting to "default") is cloned as content.
   * Elements with [data-dismiss] inside named tooltips close the pinned tooltip on click.
   */
  private _createPortaledTooltip(nameOverride?: string): void {
    if (this._portaledTooltip) return;

    const tooltip = document.createElement('as-tooltip');

    const placement = this.getAttribute('placement');
    if (placement) tooltip.setAttribute('placement', placement);
    if (this.hasAttribute('no-arrow')) tooltip.setAttribute('no-arrow', '');

    const name = nameOverride ?? this.getAttribute('active-tooltip') ?? 'default';
    const namedChild = this.querySelector(`as-tooltip[name="${name}"]`)
      ?? (name === 'default' ? this.querySelector('as-tooltip') : null);

    if (namedChild) {
      const hasInteractive = !!namedChild.querySelector('button, input, a, [tabindex], as-icon-button, as-button');
      Object.assign(tooltip.style, {
        position: 'fixed', zIndex: '10050',
        pointerEvents: hasInteractive ? 'auto' : 'none',
        maxWidth: hasInteractive ? 'none' : '',
      });
      tooltip.innerHTML = namedChild.innerHTML;
      if (hasInteractive) {
        tooltip.querySelectorAll('[data-dismiss]').forEach(el => {
          el.addEventListener('click', () => this._closePin());
        });
        tooltip.querySelectorAll('[data-confirm]').forEach(el => {
          el.addEventListener('click', () => {
            this._closePin();
            sendEv(this, 'as-tooltip-confirm');
          });
        });
      }
    } else {
      Object.assign(tooltip.style, { position: 'fixed', zIndex: '10050', pointerEvents: 'none' });
      const asText = document.createElement('as-text');
      applyLocalizedText(asText, this.getAttribute('dict-key'), this.getAttribute('default-text') ?? '');
      const vars = this.getAttribute('vars');
      if (vars) asText.setAttribute('vars', vars);
      tooltip.appendChild(asText);
    }

    document.body.appendChild(tooltip);
    this._portaledTooltip = tooltip;
  }

  /** Pin a named tooltip open (stays visible until dismissed, ESC, or outside click). */
  showNamedTooltip(name: string): void {
    this._closePin();
    this._pinned = true;
    this._shouldBeVisible = true;
    this._createPortaledTooltip(name);
    this._portaledTooltip?.setAttribute('show', '');
    this._updateTooltipPosition();
    document.addEventListener('keydown', this._handlePinKeydown);
    // Defer outside-click listener so the triggering click doesn't immediately close it
    setTimeout(() => {
      if (this._pinned) document.addEventListener('click', this._handlePinOutsideClick, true);
    }, 0);
  }

  private _handlePinKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this._closePin();
  };

  private _handlePinOutsideClick = (e: MouseEvent): void => {
    const t = e.target as Node;
    if (this._portaledTooltip?.contains(t) || this.contains(t)) return;
    this._closePin();
  };

  private _closePin(): void {
    document.removeEventListener('keydown', this._handlePinKeydown);
    document.removeEventListener('click', this._handlePinOutsideClick, true);
    this._pinned = false;
    this._shouldBeVisible = false;
    this._portaledTooltip?.removeAttribute('show');
    if (this._cleanup) { this._cleanup(); this._cleanup = undefined; }
    this._removePortaledTooltip();
  }

  /**
   * Remove the portaled tooltip from document.body
   */
  private _removePortaledTooltip(): void {
    if (this._portaledTooltip) {
      this._portaledTooltip.remove();
      this._portaledTooltip = undefined;
    }
  }

  /**
   * Handle attribute changes with a reactive approach
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    // Skip if component isn't connected yet
    if (!this.shadowRoot || !this.isConnected) return;

    // Skip if nothing changed
    if (oldValue === newValue) return;

    const tooltip = this._portaledTooltip;

    // Handle specific attribute changes
    switch (name) {
      case 'show':
        if (newValue === null) {
          this._shouldBeVisible = false;
          if (tooltip) {
            tooltip.removeAttribute('show');
          }
          // Clean up autoUpdate when hiding
          if (this._cleanup) {
            this._cleanup();
            this._cleanup = undefined;
          }
          // Remove portaled tooltip when hiding
          this._removePortaledTooltip();
        } else {
          this._shouldBeVisible = true;
          this._createPortaledTooltip();
          this._portaledTooltip?.setAttribute('show', '');
          // Update position when showing
          this._updateTooltipPosition();
        }
        break;

      case 'placement':
        // Pass placement to tooltip component for arrow positioning
        if (tooltip) {
          if (newValue === null) {
            tooltip.removeAttribute('placement');
          } else {
            tooltip.setAttribute('placement', newValue);
          }
          // Update tooltip position if currently visible
          if (tooltip.hasAttribute('show')) {
            this._updateTooltipPosition();
          }
        }
        break;

      case 'offset':
        // Update tooltip position if currently visible
        if (tooltip?.hasAttribute('show')) {
          this._updateTooltipPosition();
        }
        break;

      case 'auto-flip':
        // Update tooltip position if currently visible since flip behavior changed
        if (tooltip?.hasAttribute('show')) {
          this._updateTooltipPosition();
        }
        break;

      case 'no-arrow':
        if (tooltip) {
          if (newValue === null) {
            tooltip.removeAttribute(name);
          } else {
            tooltip.setAttribute(name, newValue ?? '');
          }
        }
        break;

      case 'dict-key':
      case 'default-text':
      case 'vars':
        if (tooltip) {
          const textElement = tooltip.querySelector('as-text') as HTMLElement | null;
          if (textElement) {
            applyLocalizedText(textElement, this.getAttribute('dict-key'), this.getAttribute('default-text') ?? '');
            const vars = this.getAttribute('vars');
            if (vars) textElement.setAttribute('vars', vars);
            else textElement.removeAttribute('vars');
          }
        }
        break;

      case 'show-on-hover':
      case 'toggle-on-click':
      case 'close-on-click':
        // These require re-rendering to update the event handlers
        this._render();
        break;

      case 'flash':
        if (newValue !== null) {
          // Flash attribute added, trigger the flash
          this._flashTooltip();
        } else {
          // Flash attribute removed, cancel any ongoing flash
          if (this._flashTimeout) {
            clearTimeout(this._flashTimeout);
            this._flashTimeout = undefined;
          }
        }
        break;

      case 'flash-duration':
        break;

      case 'active-tooltip':
        if (this._shouldBeVisible && !this._pinned && this._portaledTooltip) {
          this._removePortaledTooltip();
          this._createPortaledTooltip();
          this._portaledTooltip?.setAttribute('show', '');
          this._updateTooltipPosition();
        }
        break;
    }
  }

  /**
   * Set up event delegation
   */
  private _setupEvents() {
    const wrapper = this.shadowRoot?.querySelector('.tooltip-wrapper');
    if (!wrapper) return;

    // Use event delegation to handle all events in one place
    wrapper.addEventListener('pointerenter', this._handleEvent);
    wrapper.addEventListener('pointerleave', this._handleEvent);
    wrapper.addEventListener('click', this._handleEvent);

    // Listen for nested tooltip-wrapper hovers to hide our tooltip
    this.addEventListener('as-tooltip-hover', this._handleNestedHover);
  }
  
  /**
   * Hide tooltip when a nested tooltip-wrapper is hovered
   */
  private _handleNestedHover = (event: Event) => {
    // Don't react to our own events
    if (event.target === this) return;

    const tooltip = this._portaledTooltip;
    if (tooltip?.hasAttribute('show')) {
      tooltip.removeAttribute('show');
      this._shouldBeVisible = false;
      if (this._cleanup) {
        this._cleanup();
        this._cleanup = undefined;
      }
      this._removePortaledTooltip();
    }
  };

  /**
   * Unified event handler using dataset properties for behavior control
   */
  private _handleEvent = (event: Event) => {
    const wrapper = event.currentTarget as HTMLElement;

    // Handle hover events
    if (wrapper.dataset.hover === 'true') {
      if (event.type === 'pointerenter') {
        // Notify ancestor tooltip-wrappers to hide their tooltips
        sendEv(this, 'as-tooltip-hover');

        this._shouldBeVisible = true;
        this._createPortaledTooltip();
        this._portaledTooltip?.setAttribute('show', '');
        // Update position when showing
        this._updateTooltipPosition();
      } else if (event.type === 'pointerleave') {
        if (!this._pinned) {
          this._shouldBeVisible = false;
          this._portaledTooltip?.removeAttribute('show');
          if (this._cleanup) { this._cleanup(); this._cleanup = undefined; }
          this._removePortaledTooltip();
        }
      } else if (event.type === 'click' && wrapper.dataset.closeOnClick === 'true') {
        // Opt-in: a click on the hovered element (e.g. opening a dropdown)
        // shouldn't leave the tooltip floating over whatever it just opened.
        if (!this._pinned) {
          this._shouldBeVisible = false;
          this._portaledTooltip?.removeAttribute('show');
          if (this._cleanup) { this._cleanup(); this._cleanup = undefined; }
          this._removePortaledTooltip();
        }
      }
    }

    // Handle click events
    if (wrapper.dataset.click === 'true' && event.type === 'click') {
      if (this._portaledTooltip?.hasAttribute('show')) {
        this._shouldBeVisible = false;
        this._portaledTooltip.removeAttribute('show');
        // Clean up autoUpdate when hiding
        if (this._cleanup) {
          this._cleanup();
          this._cleanup = undefined;
        }
        this._removePortaledTooltip();
      } else {
        this._shouldBeVisible = true;
        this._createPortaledTooltip();
        this._portaledTooltip?.setAttribute('show', '');
        // Update position when showing
        this._updateTooltipPosition();
      }
    }
  }

  /**
   * Main render method
   */
  private _render() {
    if (!this.shadowRoot) return;
    
    // Clear existing content
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }
    
    // Append new template
    this.shadowRoot.appendChild(this._createTemplate());
    
    // Set up events
    this._setupEvents();
  }

  connectedCallback() {
    this._render();

    // Show immediately if show attribute is present
    if (this.hasAttribute('show')) {
      this._shouldBeVisible = true;
      this._createPortaledTooltip();
      this._portaledTooltip?.setAttribute('show', '');
      // Use setTimeout to ensure tooltip is rendered first
      setTimeout(() => this._updateTooltipPosition(), 0);
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    const wrapper = this.shadowRoot?.querySelector('.tooltip-wrapper');
    if (wrapper) {
      wrapper.removeEventListener('pointerenter', this._handleEvent);
      wrapper.removeEventListener('pointerleave', this._handleEvent);
      wrapper.removeEventListener('click', this._handleEvent);
    }

    // Clean up nested hover listener
    this.removeEventListener('as-tooltip-hover', this._handleNestedHover);

    // Clean up autoUpdate
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = undefined;
    }

    // Clean up intersection observer
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
      this._intersectionObserver = undefined;
    }

    // Clean up flash timeout
    if (this._flashTimeout) {
      clearTimeout(this._flashTimeout);
      this._flashTimeout = undefined;
    }

    document.removeEventListener('keydown', this._handlePinKeydown);
    document.removeEventListener('click', this._handlePinOutsideClick, true);
    this._pinned = false;
    // Remove portaled tooltip from body
    this._removePortaledTooltip();
  }
}

// Check if the element is already defined before registering it
if (!customElements.get(AsTooltipWrapper.tag)) {
  customElements.define(AsTooltipWrapper.tag, AsTooltipWrapper);
} else {
  console.warn("as-tooltip-wrapper already defined");
}
